import { useState } from 'react';

import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import {
  describeLocation,
  useDeliveryLocation,
  useDeviceState,
  useStates,
} from '@/lib/deliveryLocation';

/**
 * Where the buyer is having things delivered, and how they change it.
 *
 * ── WHY IT ASKS RATHER THAN GUESSING ────────────────────────────────────────
 *
 * This said "Wuse II, Abuja" to everybody in the country. A buyer in Kano read
 * a delivery address that was not theirs on the first screen of a pharmacy app,
 * which is a bad place to learn that the software is not really talking to you.
 *
 * When nothing is known it now asks. "Set your location" is honest and
 * actionable; a confident wrong answer is neither.
 *
 * ── AND WHY DEVICE LOCATION IS OFFERED, NOT TAKEN ───────────────────────────
 *
 * Asking the browser for a position fires a permission prompt, and firing one
 * unprompted on the home screen is how an app teaches people to press Block.
 * It sits behind a button somebody chooses, a refusal is a fine outcome, and
 * the state list is always there as the way that needs no permission at all.
 *
 * The coordinates never leave the device: the state is worked out here from a
 * table of centroids rather than sent to a geocoder.
 */
export function DeliverTo({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const { location, choose, hasAddress } = useDeliveryLocation();
  const label = describeLocation(location);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="deliver-to"
        className={cn(
          'flex items-center gap-2 rounded-btn text-left hover:bg-paper',
          compact ? 'min-h-9 px-1.5' : '-mx-2 min-h-11 px-2',
        )}
      >
        <Icon name="pin" size={15} strokeWidth={1.9} className="shrink-0 text-blue" />

        {label ? (
          <span className={cn('min-w-0', compact ? 'leading-none' : '')}>
            <span className={cn('block text-[11px] text-muted', compact ? 'leading-none' : '')}>
              Deliver to
            </span>
            <b className={cn('block truncate text-ink', compact ? 'text-xs' : 'text-[11px]')}>
              {label}
            </b>
          </span>
        ) : (
          // Asking, not asserting.
          <span className="text-[11px] font-semibold text-blue">Set your location</span>
        )}

        <Icon name="down" size={13} strokeWidth={2} className="shrink-0 text-muted" />
      </button>

      {open ? (
        <LocationSheet
          current={location?.state ?? null}
          hasAddress={hasAddress}
          onClose={() => setOpen(false)}
          onPick={(state, source) => {
            choose({ state, lga: null, source });
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function LocationSheet({
  current,
  hasAddress,
  onClose,
  onPick,
}: {
  current: string | null;
  hasAddress: boolean;
  onClose: () => void;
  onPick: (state: string, source: 'chosen' | 'device') => void;
}) {
  const states = useStates();
  const { ask, asking, denied } = useDeviceState();
  const [filter, setFilter] = useState('');

  const shown = states.filter((one) => one.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Delivery location"
      onClick={onClose}
    >
      <div
        className="max-h-[80dvh] w-full max-w-[420px] overflow-y-auto rounded-t-card bg-white p-4 sm:rounded-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="m-0 font-display text-[17px] font-bold">Where are you?</h2>
            <p className="m-0 mt-0.5 text-[12px] leading-relaxed text-muted">
              It decides what delivery costs and which pharmacies can reach you.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-btn text-muted hover:bg-paper"
          >
            <Icon name="x" size={16} strokeWidth={2.2} />
          </button>
        </div>

        {/* A buyer with a saved address is already answered, and changing it
            here would quietly disagree with where their order actually goes. */}
        {hasAddress ? (
          <p className="m-0 mt-3 rounded-card border border-line bg-paper px-3 py-2.5 text-[12px] leading-relaxed text-muted">
            This comes from your default delivery address. Change it in{' '}
            <b className="font-semibold text-ink">Account &rsaquo; Addresses</b> so your orders and
            this agree.
          </p>
        ) : (
          <>
            <button
              type="button"
              disabled={asking}
              data-testid="use-my-location"
              onClick={async () => {
                const found = await ask();

                if (found) {
                  onPick(found, 'device');
                }
              }}
              className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-btn bg-blue px-4 text-[13px] font-bold text-white disabled:opacity-60"
            >
              <Icon name="pin" size={15} strokeWidth={2.2} />
              {asking ? 'Finding you…' : 'Use my location'}
            </button>

            {denied ? (
              <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-caution">
                Your browser did not share a location. Pick your state below instead — it works
                just as well.
              </p>
            ) : (
              <p className="m-0 mt-2 text-[11px] leading-relaxed text-faint">
                Your position stays on your device. We work out the state here and send nothing to
                anybody.
              </p>
            )}

            <div className="mt-4">
              <input
                type="search"
                value={filter}
                placeholder="Search states"
                aria-label="Search states"
                onChange={(event) => setFilter(event.target.value)}
                className="min-h-11 w-full rounded-btn border border-line px-3 text-[13px] text-ink outline-none placeholder:text-faint focus:border-blue"
              />
            </div>

            <div className="mt-2">
              {shown.map((one) => (
                <button
                  key={one}
                  type="button"
                  onClick={() => onPick(one, 'chosen')}
                  className={cn(
                    'flex min-h-11 w-full items-center justify-between border-b border-line-2 px-1 text-left text-[13px] last:border-b-0',
                    one === current ? 'font-semibold text-blue' : 'text-ink',
                  )}
                >
                  {one}
                  {one === current ? <Icon name="check" size={15} strokeWidth={2.6} /> : null}
                </button>
              ))}

              {shown.length === 0 ? (
                <p className="m-0 py-3 text-[12.5px] text-faint">No state matches that.</p>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
